//LeftPane.js
import {
    Box,
    Button,
    Flex,
    Text,
    VStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Textarea,
    IconButton,
} from "@chakra-ui/react";
import { ArrowRight, Copy, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import ChatInput from "../components/ChatInputContainer";
import MarkdownRenderer from '../components/MarkdownRenderer';
import { useState } from 'react';

export default function LeftPane({
    messagesContainerRef,
    turns,
    activeSuggestionTurnId,
    onSendMessage,
    isLoading,
    onScrollMessages,
    isRightPaneOpen,
    typingTurnId,
    typingSuggestionId,

    onToggleSuggestion,
    reports,
    activeReportId,
    onToggleReport,
    typingReportId,
    isLoadingReport,
    onSubmitFeedback,
}) {

    // Track which message was copied (by turn.id and type: 'user' or 'bot')
    const [copiedMessage, setCopiedMessage] = useState(null);

    // Track feedback state per message: { messageId: { type: 'positive'|'negative'|null, comment: string } }
    const [feedbackState, setFeedbackState] = useState({});

    const [feedbackData, setFeedbackData] = useState({
        messageId: null,
        feedbackType: null,
        comment: ''
    });
    const { isOpen, onOpen, onClose } = useDisclosure();

    const handleCopyMessage = (text, messageId, messageType) => {
        navigator.clipboard.writeText(text);

        // Set copied state for this specific message
        setCopiedMessage({ id: messageId, type: messageType });

        // Reset after 2 seconds
        setTimeout(() => {
            setCopiedMessage(null);
        }, 2000);
    };

    const handleFeedbackClick = (messageId, feedbackType) => {
        const currentFeedback = feedbackState[messageId];
        const currentType = currentFeedback?.type;

        // Toggle behavior:
        // If clicking the same icon that's already active, we'll open dialog to remove
        // If clicking different icon, switch to that feedback
        // If no feedback yet, set new feedback

        if (currentType === feedbackType) {
            // Same icon clicked - prepare to remove feedback
            setFeedbackData({
                messageId,
                feedbackType: null, // Will remove feedback
                comment: currentFeedback?.comment || '',
                isRemoving: true
            });
        } else {
            // Different icon or new feedback
            setFeedbackData({
                messageId,
                feedbackType,
                comment: currentFeedback?.comment || '', // Preserve existing comment if updating
                isRemoving: false
            });
        }
        onOpen();
    };

    const handleFeedbackSubmit = () => {
        const { messageId, feedbackType, comment, isRemoving } = feedbackData;

        if (isRemoving) {
            // Remove feedback
            setFeedbackState(prev => ({
                ...prev,
                [messageId]: { type: null, comment: '' }
            }));

            // Call the callback to update Firestore
            if (onSubmitFeedback) {
                onSubmitFeedback(messageId, null, '');
            }
        } else {
            // Add or update feedback
            setFeedbackState(prev => ({
                ...prev,
                [messageId]: { type: feedbackType, comment: comment || '' }
            }));

            // Call the callback to update Firestore
            if (onSubmitFeedback) {
                onSubmitFeedback(messageId, feedbackType, comment || '');
            }
        }

        onClose();
        // Reset feedback data
        setFeedbackData({
            messageId: null,
            feedbackType: null,
            comment: '',
            isRemoving: false
        });
    };

    const handleFeedbackCancel = () => {
        onClose();
        setFeedbackData({
            messageId: null,
            feedbackType: null,
            comment: '',
            isRemoving: false
        });
    };

    return (
        <Flex flexDir="column" h="100%" w="100%" bg="white"
            border="1px solid #ECECEC"
            boxSizing="border-box"
            borderRadius="12px"
            // m={2}
            ml={0}
        >
            <Box flex="1" display="flex"
                overflowY="auto"
                overflowX="hidden"
                px={isRightPaneOpen ? '16px' : '0'}
                ref={messagesContainerRef}
                onScroll={onScrollMessages}
                css={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollBehavior: 'auto',
                }}>
                <Box
                    maxW={isRightPaneOpen ? '100%' : '850px'}
                    w="100%"
                    mx={isRightPaneOpen ? '0' : 'auto'}
                    py="16px"
                >
                    {turns.map((turn) => {
                        const isTyping = typingTurnId === turn.id;
                        const hasSuggestion = turn.suggestion !== undefined;
                        const isSuggestionActive = activeSuggestionTurnId === turn.id && !activeReportId;
                        const turnReports = reports[turn.id] || [];

                        // Get feedback state for this bot message
                        const botMessageFeedback = feedbackState[turn.id];
                        const isThumbsUpActive = botMessageFeedback?.type === 'positive';
                        const isThumbsDownActive = botMessageFeedback?.type === 'negative';

                        return (
                            <Box key={turn.id}
                            // mb="32px"
                            >
                                {/* USER MESSAGE */}

                                <Flex
                                    role="group"
                                    justifyContent="flex-end"
                                    // gap="8px"
                                    alignItems="flex-start"
                                    mb="8px"
                                    flexDirection="column"
                                >
                                    <Flex justifyContent="flex-end" gap="8px" alignItems="flex-start" w="100%">
                                        <Box
                                            display="inline-block"
                                            bg="#F3F3F3"
                                            color="#242424"
                                            px="16px"
                                            py="8px"
                                            borderRadius="16px"
                                            borderTopRightRadius="0"
                                            fontSize="14px"
                                            maxW="70%"
                                            wordBreak="break-word"
                                        >
                                            {/* Show current files if they exist */}
                                            {turn.currentFiles && turn.currentFiles.length > 0 && (
                                                <Flex
                                                    flexWrap="wrap"
                                                    gap="4px"
                                                    mb={turn.userText ? "8px" : "0"}
                                                >
                                                    {turn.currentFiles.map((file, index) => (
                                                        <Box
                                                            key={index}
                                                            bg="#ffffff"
                                                            borderRadius="100px"
                                                            border="1px solid #DADADA"
                                                            px="10px"
                                                            py="6px"
                                                            fontSize="12px"
                                                            fontWeight="semibold"
                                                            display="flex"
                                                            alignItems="center"
                                                            gap="8px"
                                                            color="#242424"
                                                            // isUploading={file.uploading} // ← Show spinner/loading state
                                                            // hasError={file.uploadError} // ← Show error state
                                                            // downloadURL={file.downloadURL} // ← Available after upload
                                                        >
                                                            <span>{file.filename || file.name}</span>
                                                        </Box>
                                                    ))}
                                                </Flex>
                                            )}

                                            {/* Show text if it exists */}
                                            {turn.userText && <Text mt={turn.currentFiles?.length > 0 ? "8px" : "0"}>{turn.userText}</Text>}

                                            {/* Fallback if neither exists */}
                                            {!turn.userText && (!turn.currentFiles || turn.currentFiles.length === 0) && (
                                                <Text>[Empty message]</Text>
                                            )}
                                        </Box>

                                        <Box
                                            w="40px"
                                            h="40px"
                                            borderRadius="full"
                                            bg="#F3F3F3"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="14px"
                                            fontWeight="semibold"
                                            color="#7A7A7A"
                                        >
                                            A
                                        </Box>
                                    </Flex>

                                    {/* Copy Button - Always takes space (24px height), visible on hover */}
                                    <Flex justifyContent="flex-end" w="100%" pr="48px" pt={1}>
                                        <Box h="24px" display="flex" alignItems="center">
                                            <Button
                                                // size="sm"
                                                // variant="ghost"
                                                opacity={0}
                                                _groupHover={{ opacity: 1 }}
                                                transition="opacity 0.2s"
                                                onClick={() => handleCopyMessage(
                                                    turn.userText || turn.currentFiles?.map(f => f.name).join(', ') || '',
                                                    turn.id,
                                                    'user'
                                                )}
                                                minW="unset"
                                                h="24px"
                                                w="24px"
                                                p="0"
                                                _hover={{ bg: "#F3F3F3" }}
                                                bg="transparent"
                                            >
                                                {copiedMessage?.id === turn.id && copiedMessage?.type === 'user' ? (
                                                    <Check size={16}
                                                    // color="#10B981" 
                                                    />
                                                ) : (
                                                    <Copy size={16} />
                                                )}
                                            </Button>
                                        </Box>
                                    </Flex>
                                </Flex>


                                {/* BOT MESSAGE */}
                                <Flex justifyContent="flex-start" mb="12px" role="group">

                                    <Flex direction="column" alignItems="flex-start" w="100%">
                                        <Box
                                            display="inline-block"
                                            bg="white"
                                            px="16px"
                                            py="8px"
                                            borderRadius="16px"
                                            fontSize="14px"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            maxW="75%"
                                            // whiteSpace="pre-wrap"
                                            wordBreak="break-word"
                                        >
                                            {/* {turn.botFollowup} */}
                                            <MarkdownRenderer content={turn.botFollowup} />
                                            {/* TYPING INDICATOR */}
                                            {isTyping && (
                                                <Flex mt="8px" color="gray.400" fontSize="12px" gap="4px">
                                                    <Box as="span" display="inline-block" animation="bounce 1s infinite">
                                                        •
                                                    </Box>
                                                    <Box
                                                        as="span"
                                                        display="inline-block"
                                                        animation="bounce 1s infinite"
                                                        style={{ animationDelay: '0.1s' }}
                                                    >
                                                        •
                                                    </Box>
                                                    <Box
                                                        as="span"
                                                        display="inline-block"
                                                        animation="bounce 1s infinite"
                                                        style={{ animationDelay: '0.2s' }}
                                                    >
                                                        •
                                                    </Box>
                                                </Flex>
                                            )}
                                        </Box>
                                        {/* Copy Button - Shows on hover */}
                                        {turn.botFollowup && (
                                            <Flex justifyContent="flex-start" w="100%" pl="10px" pt={1}>

                                                <IconButton
                                                    onClick={() => handleFeedbackClick(turn.id, 'positive')}
                                                    minW="unset"
                                                    h="24px"
                                                    w="24px"
                                                    p={0}
                                                    _hover={{ bg: '#F3F3F3' }}
                                                    bg={isThumbsUpActive ? '#E8F5E9' : 'transparent'}
                                                    aria-label="Thumbs up"
                                                    icon={<ThumbsUp size={16} color={isThumbsUpActive ? '#4CAF50' : '#666'} fill={'none'} />}
                                                />

                                                {/* Thumbs Down Button */}
                                                <IconButton
                                                    onClick={() => handleFeedbackClick(turn.id, 'negative')}
                                                    minW="unset"
                                                    h="24px"
                                                    w="24px"
                                                    p={0}
                                                    _hover={{ bg: '#F3F3F3' }}
                                                    bg={isThumbsDownActive ? '#FFEBEE' : 'transparent'}
                                                    aria-label="Thumbs down"
                                                    icon={<ThumbsDown size={16} color={isThumbsDownActive ? '#F44336' : '#666'} fill={'none'} />}
                                                />

                                                <IconButton
                                                    onClick={() => handleCopyMessage(turn.botFollowup, turn.id, 'bot')}
                                                    minW="unset"
                                                    h="24px"
                                                    w="24px"
                                                    p={0}
                                                    _hover={{ bg: '#F3F3F3' }}
                                                    bg="transparent"
                                                    aria-label="Copy message"
                                                    icon={
                                                        copiedMessage?.id === turn.id && copiedMessage?.type === 'bot' ? (
                                                            <Check size={16} />
                                                        ) : (
                                                            <Copy size={16} />
                                                        )
                                                    }
                                                />

                                                {/* <Box h="24px" display="flex" alignItems="center">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        opacity={0}
                                                        _groupHover={{ opacity: 1 }}
                                                        transition="opacity 0.2s"
                                                        onClick={() => handleCopyMessage(turn.botFollowup, turn.id, 'bot')}
                                                        minW="unset"
                                                        h="24px"
                                                        w="24px"
                                                        p={0}
                                                        _hover={{ bg: '#F3F3F3' }}
                                                        bg="transparent"
                                                    >
                                                        {copiedMessage?.id === turn.id && copiedMessage?.type === 'bot' ? (
                                                            <Check size={16}
                                                            // color="#10B981" 
                                                            />
                                                        ) : (
                                                            <Copy size={16} />
                                                        )}
                                                    </Button>
                                                </Box> */}
                                            </Flex>
                                        )}
                                    </Flex>
                                </Flex>

                                {/* SHOW SUGGESTIONS BUTTON */}
                                {hasSuggestion && (
                                    <>
                                        <Button
                                            w="100%"
                                            mb="12px"
                                            h="75px"
                                            borderRadius="12px"
                                            // ✅ Conditional background based on active state
                                            bg={isSuggestionActive
                                                ? 'linear-gradient(180deg, rgba(255, 200, 235, 1) 0%, rgba(200, 235, 255, 1) 100%)'
                                                : 'linear-gradient(180deg, rgba(255, 230, 250, 0.8) 0%, rgba(230, 250, 255, 0.8) 100%)'
                                            }
                                            border="1px solid"
                                            // ✅ Conditional border based on active state
                                            borderColor={isSuggestionActive ? "#FF9CEE" : "#DADADA"}
                                            color="#242424"
                                            fontSize="14px"
                                            fontWeight="medium"
                                            textAlign="left"
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="flex-start"
                                            position="relative"
                                            pl="16px"
                                            // ✅ Optional: Add box shadow when active
                                            boxShadow={isSuggestionActive ? '0 4px 12px rgba(207, 0, 138, 0.2)' : 'none'}
                                            _hover={{
                                                bg: 'linear-gradient(180deg, rgba(255, 220, 245, 1) 0%, rgba(220, 245, 255, 1) 100%)',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 12px rgba(207, 0, 138, 0.15)'
                                            }}
                                            _active={{
                                                transform: 'translateY(0)',
                                                boxShadow: '0 2px 6px rgba(207, 0, 138, 0.1)'
                                            }}
                                            transition="all 0.2s ease"
                                            onClick={() => onToggleSuggestion(turn.id)}
                                        >
                                            <Flex alignItems="center" gap="8px" mb="4px">
                                                <Box
                                                    width="16px"
                                                    height="16px"
                                                    backgroundImage="url('/icon_btn_pink.svg')"
                                                    backgroundSize="contain"
                                                    backgroundRepeat="no-repeat"
                                                    backgroundPosition="center"
                                                />
                                                <Text fontSize="14px" fontWeight="600" color="#242424">
                                                    {typingSuggestionId === turn.id ? 'Generating suggestions...' : 'Suggestions generated'}
                                                </Text>
                                            </Flex>

                                            <Box
                                                position="absolute"
                                                right="20px"
                                                top="50%"
                                                transform="translateY(-50%)"
                                            >
                                                <ArrowRight size={20} color="#CF008A" />
                                            </Box>
                                        </Button>

                                        {/* Report Buttons */}
                                        {turnReports.map((report) => {
                                            const isReportActive = activeReportId?.turnId === turn.id && activeReportId?.version === report.version;
                                            const reportLabel = report.version === 1 ? 'Report' : `Report V${report.version}`;

                                            // Check if THIS specific report is being generated/streamed
                                            const isThisReportGenerating =
                                                (typingReportId?.turnId === turn.id && typingReportId?.version === report.version) ||
                                                (isLoadingReport && activeReportId?.turnId === turn.id && activeReportId?.version === report.version);

                                            // Check if report is complete
                                            const isReportComplete = report.isComplete === true;

                                            // Determine button text
                                            const reportButtonText = isThisReportGenerating
                                                ? 'Generating report...'
                                                : (isReportComplete ? 'Report generated' : 'Report generated');

                                            return (
                                                <Button
                                                    key={report.version}
                                                    onClick={() => onToggleReport(turn.id, report.version)}
                                                    w="100%"
                                                    mt="12px"
                                                    mb="12px"
                                                    h="75px"
                                                    borderRadius="12px"
                                                    // ✅ Conditional background based on active state
                                                    bg={isReportActive
                                                        ? 'linear-gradient(180deg, rgba(255, 200, 235, 1) 0%, rgba(200, 235, 255, 1) 100%)'
                                                        : 'linear-gradient(180deg, rgba(255, 230, 250, 0.8) 0%, rgba(230, 250, 255, 0.8) 100%)'
                                                    }
                                                    border="1px solid"
                                                    // ✅ Conditional border based on active state
                                                    borderColor={isReportActive ? "#FF9CEE" : "#DADADA"}
                                                    color="#242424"
                                                    fontSize="14px"
                                                    fontWeight="medium"
                                                    textAlign="left"
                                                    display="flex"
                                                    flexDirection="column"
                                                    alignItems="flex-start"
                                                    position="relative"
                                                    pl="16px"
                                                    // ✅ Optional: Add box shadow when active
                                                    boxShadow={isReportActive ? '0 4px 12px rgba(207, 0, 138, 0.2)' : 'none'}
                                                    _hover={{
                                                        bg: 'linear-gradient(180deg, rgba(255, 220, 245, 1) 0%, rgba(220, 245, 255, 1) 100%)',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 4px 12px rgba(207, 0, 138, 0.15)'
                                                    }}
                                                    _active={{
                                                        transform: 'translateY(0)',
                                                        boxShadow: '0 2px 6px rgba(207, 0, 138, 0.1)'
                                                    }}
                                                    transition="all 0.2s ease"
                                                >
                                                    <Flex alignItems="center" gap="8px" mb="4px">

                                                        <Text fontSize="14px" fontWeight="600" color="#242424">
                                                            {/* Report generated */}
                                                            {reportButtonText}
                                                        </Text>
                                                    </Flex>

                                                    <Text fontSize="13px" fontWeight="500" color="#CF008A">
                                                        V{report.version}
                                                    </Text>

                                                    {/* Arrow Icon */}
                                                    <Box
                                                        position="absolute"
                                                        right="20px"
                                                        top="50%"
                                                        transform="translateY(-50%)"
                                                    >
                                                        <ArrowRight size={20} color="#CF008A" />
                                                    </Box>
                                                </Button>
                                            );
                                        })}
                                    </>
                                )}


                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box
                flexShrink={0}
                bg="#fff"
                pt="12px"
                pb="12px"
                px={isRightPaneOpen ? '16px' : '0'}
                borderRadius="16px"
            >
                <Box maxW={isRightPaneOpen ? '100%' : '850px'} w="100%" mx={isRightPaneOpen ? '0' : 'auto'}>
                    <ChatInput isLoading={isLoading} onSend={onSendMessage} mode="chat" disabled={typingTurnId !== null || typingSuggestionId !== null} />
                </Box>
            </Box>

            {/* Feedback Dialog Modal */}
            {/* <Modal isOpen={isOpen} onClose={handleFeedbackCancel} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Provide Feedback</ModalHeader>
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Flex alignItems="center" gap={2}>
                                <Text fontWeight="medium">Selected feedback:</Text>
                                <Flex alignItems="center" gap={1}>
                                    {feedbackData.feedbackType === 'thumbs-up' ? (
                                        <>
                                            <ThumbsUp size={18} />
                                            <Text>Thumbs Up</Text>
                                        </>
                                    ) : (
                                        <>
                                            <ThumbsDown size={18} />
                                            <Text>Thumbs Down</Text>
                                        </>
                                    )}
                                </Flex>
                            </Flex>

                            <Box>
                                <Text mb={2} fontSize="sm" color="gray.600">
                                    Additional comments (optional)
                                </Text>
                                <Textarea
                                    value={feedbackData.comment}
                                    onChange={(e) => setFeedbackData({ ...feedbackData, comment: e.target.value })}
                                    placeholder="Share your thoughts..."
                                    rows={4}
                                />
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter gap={3}>
                        <Button variant="ghost" onClick={handleFeedbackCancel}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleFeedbackSubmit}>
                            Send
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal> */}

            <Modal isOpen={isOpen} onClose={handleFeedbackCancel} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {feedbackData.isRemoving ? 'Remove Feedback' : 'Provide Feedback'}
                    </ModalHeader>
                    <ModalBody>
                        {feedbackData.isRemoving ? (
                            <Text mb={4}>Are you sure you want to remove your feedback?</Text>
                        ) : (
                            <>
                                <Text mb={2} fontWeight="medium">Selected feedback:</Text>
                                <Flex alignItems="center" gap={2} mb={4}>
                                    {feedbackData.feedbackType === 'positive' ? (
                                        <>
                                            <ThumbsUp size={20} color="#4CAF50" />
                                            <Text color="#4CAF50" fontWeight="medium">Thumbs Up</Text>
                                        </>
                                    ) : (
                                        <>
                                            <ThumbsDown size={20} color="#F44336" />
                                            <Text color="#F44336" fontWeight="medium">Thumbs Down</Text>
                                        </>
                                    )}
                                </Flex>
                            </>
                        )}

                        {!feedbackData.isRemoving && (
                            <>
                                <Text mb={2} fontSize="14px" color="#666">
                                    Additional comments (optional)
                                </Text>
                                <Textarea
                                    value={feedbackData.comment}
                                    onChange={(e) => setFeedbackData({ ...feedbackData, comment: e.target.value })}
                                    placeholder="Share your thoughts..."
                                    rows={4}
                                />
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={handleFeedbackCancel}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme={feedbackData.isRemoving ? "red" : "blue"}
                            onClick={handleFeedbackSubmit}
                        >
                            {feedbackData.isRemoving ? 'Remove' : 'Submit'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </Flex>
    );
}